import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/api";
import { PageTitle, GlassCard, EmptyState } from "@/components/ui-kit";
import { Trophy, Crown, Medal, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function HonorBoard() {
  const now = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(now);
  const [board, setBoard] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/stats/honor-board?month=${month}`).then((r) => setBoard(r.data.board || [])).catch(() => {});
  }, [month]);

  const top3 = board.slice(0, 3);
  const rest = board.slice(3);
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);
  const heights = { 1: "h-40", 2: "h-32", 3: "h-24" };
  const colors = { 1: "#FFD700", 2: "#C0C0C0", 3: "#CD7F32" };

  return (
    <div>
      <PageTitle title="لوحة الشرف" subtitle="تكريم المنسقين المتميزين — وسام التميز الشهري" icon={Trophy}
        actions={<input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 rounded-xl bg-amber-50 border border-amber-400/30 text-stone-800 text-sm" />}
      />

      {!board.length ? <EmptyState text="لا توجد إنجازات مسجلة لهذا الشهر" icon={Trophy} /> : (
        <>
          {/* Podium */}
          <div className="flex items-end justify-center gap-3 sm:gap-6 mb-8 mt-4">
            {podiumOrder.map((b) => (
              <motion.div key={b.school_id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: b.rank * 0.1 }}
                onClick={() => setDetail(b)} className="flex flex-col items-center cursor-pointer w-24 sm:w-40">
                <div className="relative mb-2">
                  {b.oscar && <div className="absolute inset-0 rounded-full blur-xl animate-pulse" style={{ background: "#FFD70066" }} />}
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-2" style={{ borderColor: colors[b.rank], background: `${colors[b.rank]}22` }}>
                    {b.rank === 1 ? <Crown size={32} style={{ color: colors[1] }} /> : <Medal size={28} style={{ color: colors[b.rank] }} />}
                  </div>
                </div>
                <div className="text-center mb-2">
                  <div className="text-sm font-semibold text-stone-800 truncate w-full">{b.coordinator_name || b.school_name}</div>
                  <div className="text-[11px] text-stone-500 truncate w-full">{b.school_name}</div>
                  <div className="text-xs text-amber-600 font-bold">{b.hours} ساعة</div>
                  {b.oscar && <div className="text-[10px] text-amber-600 flex items-center justify-center gap-1 mt-1"><Star size={10} /> وسام التميز</div>}
                </div>
                <div className={`w-full ${heights[b.rank]} rounded-t-xl glass flex items-start justify-center pt-2 font-head font-black text-2xl`} style={{ color: colors[b.rank] }}>{b.rank}</div>
              </motion.div>
            ))}
          </div>

          {rest.length > 0 && (
            <GlassCard>
              <div className="space-y-2">
                {rest.map((b) => (
                  <div key={b.school_id} onClick={() => setDetail(b)} className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-amber-100/70 transition cursor-pointer">
                    <div className="w-7 h-7 rounded-full bg-amber-500/15 text-amber-600 flex items-center justify-center text-xs font-bold">{b.rank}</div>
                    <div className="flex-1 min-w-0"><div className="text-sm text-stone-800 font-medium truncate">{b.coordinator_name || "—"}</div><div className="text-xs text-stone-500 truncate">{b.school_name}</div></div>
                    <div className="text-sm font-bold text-amber-600">{b.hours} س</div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </>
      )}

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-md" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">قصة التميز</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-center space-y-3">
              <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center bg-amber-500/15 border-2 border-amber-400/40">
                {detail.oscar ? <Crown size={36} className="text-amber-600" /> : <Trophy size={32} className="text-amber-600" />}
              </div>
              <h3 className="font-head font-bold text-xl text-stone-800">{detail.coordinator_name || detail.school_name}</h3>
              <p className="text-sm text-stone-500">{detail.school_name}</p>
              {detail.oscar && <div className="text-amber-600 text-sm flex items-center justify-center gap-1"><Star size={14} /> حائز على وسام التميز الشهري</div>}
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="bg-amber-50 rounded-xl py-3"><div className="text-2xl font-bold text-amber-600">{detail.hours}</div><div className="text-xs text-stone-500">ساعة</div></div>
                <div className="bg-amber-50 rounded-xl py-3"><div className="text-2xl font-bold text-amber-600">{detail.volunteers}</div><div className="text-xs text-stone-500">متطوع</div></div>
                <div className="bg-amber-50 rounded-xl py-3"><div className="text-2xl font-bold text-amber-600">{detail.challenge_points}</div><div className="text-xs text-stone-500">نقاط تحدي</div></div>
              </div>
              <p className="text-sm text-stone-600 pt-2">قاد فريقه لتحقيق {detail.participations} مشاركة تطوعية خلال الشهر، تاركاً بصمة خير في مجتمعه.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
